import React from 'react';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, MenuItem, Button } from '@freecodecamp/ui';
import { useTranslation } from 'react-i18next';

import { CompletedChallenge } from '../../redux/prop-types';
import { getSolutionDisplayType } from '../../utils/solution-display-type';

interface Props {
  completedChallenge: CompletedChallenge;
  projectTitle: string;
  showUserCode: () => void;
  showProjectPreview?: () => void;
  showExamResults?: () => void;
  displayContext: 'timeline' | 'settings' | 'certification';
}

export function SolutionDisplayWidget({
  completedChallenge,
  projectTitle,
  showUserCode,
  showProjectPreview,
  showExamResults,
  displayContext
}: Props): JSX.Element | null {
  const { id, solution, githubLink } = completedChallenge;
  const { t } = useTranslation();
  const viewText = t('buttons.view');
  const viewCode = t('buttons.view-code');
  const viewProject = t('buttons.view-project');
  // We need to add a random number for dropdown button id's since there may be
  // two dropdowns for the same project on the page.
  const randomIdSuffix = Math.floor(Math.random() * 1_000_000);
  const ShowFilesSolutionForCertification = (
    <button block={true} onClick={showUserCode}>
      {viewText}{' '}
      <span className='sr-only'>
        {t('settings.labels.solution-for', { projectTitle })}
      </span>
    </button>
  );
  const ShowProjectAndGithubLinkForCertification = (
    <Dropdown id={`dropdown-for-${id}-${randomIdSuffix}`} >
      <Dropdown.Toggle >
        {viewText}{' '}
        <span className='sr-only'>
          {t('settings.labels.solution-for', { projectTitle })}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <MenuItem
          variant='primary'
          // This expression is only to resolve TypeScript error.
          // There won't be a case where the link has an invalid `href`
          // as this component is only rendered if `solution` is truthy.
          href={solution ?? undefined}
          rel='noopener noreferrer'
          target='_blank'
        >
          {t('certification.project.solution')}{' '}
          <span className='sr-only'>({t('aria.opens-new-window')})</span>
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </MenuItem>
        <MenuItem
          variant='primary'
          href={githubLink}
          rel='noopener noreferrer'
          target='_blank'
        >
          {t('certification.project.source')}{' '}
          <span className='sr-only'>({t('aria.opens-new-window')})</span>
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </MenuItem>
      </Dropdown.Menu>
    </Dropdown>
  );
  const ShowProjectLinkForCertification = (
    <button
      block={true}
      // This expression is only to resolve TypeScript error.
      // There won't be a case where the link has an invalid `href`
      // as this component is only rendered if `solution` is truthy.
      href={solution ?? undefined}
      rel='noopener noreferrer'
      target='_blank'
    >
      done
      <span className='sr-only'>
        {t('settings.labels.solution-for', { projectTitle })} (
        {t('aria.opens-new-window')})
      </span>
    </button>
  );
  const MissingSolutionComponentForCertification = (
    <>{t('certification.project.no-solution')}</>
  );
  const ShowUserCode = (
    <button block={true} variant='primary' onClick={showUserCode}>
      {/* {viewText}{' '}
      <span className='sr-only'>
        {t('settings.labels.solution-for', { projectTitle })}
      </span> */}
      Done
    </button>
  );
  const ShowMultifileProjectSolution = (
    <div>
      <Dropdown id={`dropdown-for-${id}-${randomIdSuffix}`}>
        <Dropdown.Toggle className="drop-down-new" >
          {viewText}{' '}
          <span className='sr-only'>
            {t('settings.labels.solution-for', { projectTitle })}
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <MenuItem variant='primary' onClick={showUserCode} className="menu-button-drop-down">
            {viewCode}
          </MenuItem>
          <MenuItem variant='primary' onClick={showProjectPreview} className="menu-button-drop-down">
            {viewProject}
          </MenuItem>
        </Dropdown.Menu>
      </Dropdown>

      <style>
        {
          `
          .drop-down-new{

            background:#0a0a23;
            color:white;
            border:none;
            outline:none;
            padding:10px 30px;
          }
          .drop-down-new:hover{

            background:#0a0a23;
            color:white;
            border:none;
            outline:none;
            scale:none;

          }
          .menu-button-drop-down{

            background:#0a0a23;
            color:white;
            border:none;
            outline:none;
            padding:10px 30px;
          }
          .menu-button-drop-down:hover{

            background:#0a0a23;
            color:white;
            border:none;
            outline:none;
            scale:none;

          }
          
          `
        }
      </style>
    </div>
  );

  const ShowProjectAndGithubLinks = (
    <div>
      <Dropdown id={`dropdown-for-${id}-${randomIdSuffix}`}>
        <Dropdown.Toggle>
          {viewText}{' '}
          <span className='sr-only'>
            {t('settings.labels.solution-for', { projectTitle })}
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <MenuItem
            variant='primary'
            // This expression is only to resolve TypeScript error.
            // There won't be a case where the link has an invalid `href`
            // as this component is only rendered if `solution` is truthy.
            href={solution ?? undefined}
            rel='noopener noreferrer'
            target='_blank'
          >
            {t('certification.project.solution')}{' '}
            <span className='sr-only'>({t('aria.opens-new-window')})</span>
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </MenuItem>
          <MenuItem
            variant='primary'
            href={githubLink}
            rel='noopener noreferrer'
            target='_blank'
          >
            {t('certification.project.source')}{' '}
            <span className='sr-only'>({t('aria.opens-new-window')})</span>
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </MenuItem>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
  const ShowProjectLink = (
    <button
      block={true}
      variant='primary'
      // This expression is only to resolve TypeScript error.
      // There won't be a case where the link has an invalid `href`
      // as this component is only rendered if `solution` is truthy.
      href={solution ?? undefined}
      rel='noopener noreferrer'
      target='_blank'
    >
      {/* {viewText}{' '}
      <span className='sr-only'>
        {t('settings.labels.solution-for', { projectTitle })} (
        {t('aria.opens-new-window')})
      </span>
      <FontAwesomeIcon icon={faExternalLinkAlt} /> */}
      Done
    </button>
  );
  const ShowExamResults = (
    <button block={true} variant='primary' onClick={showExamResults}>
      {/* {viewText}{' '}
      <span className='sr-only'>
        {t('settings.labels.results-for', { projectTitle })}
      </span> */}
      Done
    </button>
  );
  const MissingSolutionComponent =
    displayContext === 'settings' ? (
      <>{t('certification.project.no-solution')}</>
    ) : null;

  const displayComponents =
    displayContext === 'certification'
      ? {
          showUserCode: ShowFilesSolutionForCertification,
          showMultifileProjectSolution: ShowMultifileProjectSolution,
          showProjectAndGithubLinks: ShowProjectAndGithubLinkForCertification,
          showProjectLink: ShowProjectLinkForCertification,
          showExamResults: ShowExamResults,
          none: MissingSolutionComponentForCertification
        }
      : {
          showUserCode: ShowUserCode,
          showMultifileProjectSolution: ShowMultifileProjectSolution,
          showProjectAndGithubLinks: ShowProjectAndGithubLinks,
          showProjectLink: ShowProjectLink,
          showExamResults: ShowExamResults,
          none: MissingSolutionComponent
        };

  return displayComponents[getSolutionDisplayType(completedChallenge)];
}
